import { Doc } from '@dadohr/shared/domainTypes/document';
import { SlackExperienceMessageResponse } from '@dadohr/shared/domainTypes/integrations/slack';
import {
  ScheduleChannelType,
  ScheduleStatus,
  ScheduleType
} from '@dadohr/shared/domainTypes/schedules';
import { FS } from '@dadohr/shared/versions';
import {
  Box as IconBuilds,
  Brain as IconAiUsage,
  Clock as IconSchedules,
  Cpu as IconServices,
  Crosshair as IconMoments,
  DollarSign as IconPricing,
  Flag as IconFlag,
  Server as IconOrgs,
  Settings as IconSettings,
  Sliders as IconOps,
  Users as IconAdminUsers,
  Zap as IconExperiences
} from 'lucide-react';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PageFeatureFlags } from './features/Admin/FeatureFlags';
import { PageAdminUsersOverview } from './features/AdminUsers/pages/Overview';
import { PageBuildDetails } from './features/Deployments/pages/BuildDetails';
import { PageDeploymentBuilds } from './features/Deployments/pages/Builds';
import { PageServiceDetails } from './features/Deployments/pages/ServiceDetails';
import { PageDeploymentServices } from './features/Deployments/pages/Services';
import { PageExperiencesInstances } from './features/Experiences/pages/Instances';
import { PageExperiencesTemplates } from './features/Experiences/pages/Templates';
import {
  CollectionDefinition,
  PageFirestoreInspector
} from './features/FirestoreInspector';
import { PageGlobalConfig } from './features/GlobalConfig';
import { PageLateRemindersOverview } from './features/LateReminders/pages/Overview';
import { PageLinkShortener } from './features/LinkShortener';
import { PageMigrations } from './features/Migrations';
import { PageMomentsAdmin } from './features/Moments/pages/Admin';
import { PageOrgDetails } from './features/Orgs/pages/Details';
import { PageOrgExperienceTemplateDetailsAuditLog } from './features/Orgs/pages/OrgExperienceTemplateAuditLog';
import { PageOrgExperienceTemplateDetails } from './features/Orgs/pages/OrgExperienceTemplateDetails';
import { PageOrgsOverview } from './features/Orgs/pages/Overview';
import { PageOrgSchedulesHanging } from './features/Orgs/pages/SchedulesHanging';
import { PagePricing } from './features/Pricing';
import { PageSchedulesOverview } from './features/Schedules/pages/Overview';
import { PageTemplateCopy } from './features/TemplateCopy';
import { PageInstanceRepair } from './features/InstanceRepair';
import { PageTestAccountsOverview } from './features/TestAccounts/pages/Overview';
import { PageAiUsageOverview } from './features/AiUsage/pages/Overview';
import { PageAiUsageOrgDetails } from './features/AiUsage/pages/OrgDetails';
import { sendToSlackLog } from './services/slack';

const getFieldsForCollection = (collection: keyof typeof FS) => {
  switch (collection) {
    case 'experiences':
      return [
        { name: 'orgId', values: [] },
        { name: 'templateId', values: [] }
      ];
    case 'experienceTemplates':
      return [{ name: 'orgId', values: [] }];
    case 'schedules':
      return [
        { name: 'orgId', values: [] },
        { name: 'templateId', values: [] },
        { name: 'experienceId', values: [] },
        { name: 'actor', values: [] },
        { name: 'type', values: Object.keys(ScheduleType) },
        {
          name: 'channel.type',
          values: [ScheduleChannelType.EMAIL, ScheduleChannelType.SLACK]
        },
        {
          name: 'status',
          values: [
            ScheduleStatus.PENDING,
            ScheduleStatus.SKIPPED,
            ScheduleStatus.SUCCESS,
            ScheduleStatus.RETRY,
            ScheduleStatus.ERROR
          ]
        }
      ];
    case 'automationSchedules':
      return [
        { name: 'orgId', values: [] },
        { name: 'templateId', values: [] },
        { name: 'automationTemplateId', values: [] },
        { name: 'experienceId', values: [] },
        {
          name: 'status',
          values: [
            ScheduleStatus.PENDING,
            ScheduleStatus.SKIPPED,
            ScheduleStatus.SUCCESS,
            ScheduleStatus.RETRY,
            ScheduleStatus.ERROR
          ]
        }
      ];
    case 'users':
      return [
        { name: 'orgId', values: [] },
        { name: 'firstName', values: [] },
        { name: 'preferredName', values: [] },
        { name: 'lastName', values: [] }
      ];
    case 'slackExperienceMessagesV1':
      return [
        { name: 'orgId', values: [] },
        { name: 'status', values: ['OK', 'ERROR'] }
      ];
    default:
      return [{ name: 'orgId', values: [] }];
  }
};

const getActionsForCollection = (collection: keyof typeof FS) => {
  switch (collection) {
    case 'slackExperienceMessagesV1':
      return (d: Doc<SlackExperienceMessageResponse>) => {
        return [
          {
            key: 'sendToLog',
            label: 'Send to Slack Log',
            onClick: () => sendToSlackLog(d.id)
          }
        ];
      };
    default:
      return undefined;
  }
};

const FIRESTORE_INSPECTOR_COLLECTIONS: CollectionDefinition[] = Object.entries(
  FS
).map(([key, value]) => ({
  name: value,
  fields: getFieldsForCollection(key as keyof typeof FS),
  actions: getActionsForCollection(key as keyof typeof FS)
}));

export interface ITab {
  key: string;
  path: string;
  label: string;
  render: (props: RouteComponentProps<any>) => React.ReactNode;
}

export interface IPage {
  path: string;
  title: string;
  tabs: ITab[];
}

export interface ILoosePage<T = any> {
  path: string;
  render: (props: RouteComponentProps<T>) => JSX.Element;
}

export const PAGES: IPage[] = [
  {
    path: '/orgs',
    title: 'Organizations',
    tabs: [
      {
        key: 'overview',
        path: 'overview',
        label: 'Overview',
        render: () => <PageOrgsOverview />
      }
    ]
  },
  {
    path: '/experiences',
    title: 'Experienes',
    tabs: [
      {
        key: 'instances',
        path: 'instances',
        label: 'Instances',
        render: () => <PageExperiencesInstances />
      },
      {
        key: 'templates',
        path: 'templates',
        label: 'Templates',
        render: () => <PageExperiencesTemplates />
      }
    ]
  },
  {
    path: '/moments',
    title: 'Moments',
    tabs: [
      {
        key: 'admin',
        path: 'admin',
        label: 'Admin',
        render: () => <PageMomentsAdmin />
      }
    ]
  },
  {
    path: '/schedules',
    title: 'Schedules',
    tabs: [
      {
        key: 'overview',
        path: 'overview',
        label: 'Overview',
        render: () => <PageSchedulesOverview />
      }
    ]
  },
  {
    path: '/late-reminders',
    title: 'Late Reminders',
    tabs: [
      {
        key: 'overview',
        path: 'overview',
        label: 'Overview',
        render: () => <PageLateRemindersOverview />
      }
    ]
  },
  {
    path: '/admin-users',
    title: 'Admin Users',
    tabs: [
      {
        key: 'overview',
        path: 'overview',
        label: 'Overview',
        render: () => <PageAdminUsersOverview />
      }
    ]
  },
  {
    path: '/ops',
    title: 'Operations',
    tabs: [
      {
        key: 'migrations',
        path: 'migrations',
        label: 'Migrations',
        render: () => <PageMigrations />
      },
      {
        key: 'testAccounts',
        path: 'test-accounts',
        label: 'Test Accounts',
        render: () => <PageTestAccountsOverview />
      },

      {
        key: 'templateCopy',
        path: 'template-copy',
        label: 'Template Copy',
        render: () => <PageTemplateCopy />
      },

      {
        key: 'instanceRepair',
        path: 'instance-repair',
        label: 'Instance Repair',
        render: () => <PageInstanceRepair />
      },

      {
        key: 'firestoreInspector',
        path: 'firestore-inspector',
        label: 'Firestore Inspector',
        render: () => (
          <PageFirestoreInspector
            collections={FIRESTORE_INSPECTOR_COLLECTIONS}
          />
        )
      },

      {
        key: 'linkShortener',
        path: 'link-shortener',
        label: 'Link Shortener',
        render: () => <PageLinkShortener />
      }
    ]
  },
  {
    path: '/deployments/services',
    title: 'Deployments',
    tabs: [
      {
        key: 'services',
        path: 'overview',
        label: 'Services',
        render: () => <PageDeploymentServices />
      }
    ]
  },
  {
    path: '/deployments/builds',
    title: 'Deployments',
    tabs: [
      {
        key: 'builds',
        path: 'overview',
        label: 'Builds',
        render: () => <PageDeploymentBuilds />
      }
    ]
  },
  {
    path: '/admin',
    title: 'Admin',
    tabs: [
      {
        key: 'featureFlags',
        path: 'feature-flags',
        label: 'Feature Flags',
        render: () => <PageFeatureFlags />
      }
    ]
  },
  {
    path: '/config',
    title: 'Admin',
    tabs: [
      {
        key: 'config',
        path: 'config',
        label: 'Global Config',
        render: () => <PageGlobalConfig />
      }
    ]
  },
  {
    path: '/pricing',
    title: 'Business',
    tabs: [
      {
        key: 'pricing',
        path: 'pricing',
        label: 'Pricing Calculator',
        render: () => <PagePricing />
      }
    ]
  },
  {
    path: '/ai-usage',
    title: 'AI Usage',
    tabs: [
      {
        key: 'overview',
        path: 'overview',
        label: 'Overview',
        render: () => <PageAiUsageOverview />
      }
    ]
  }
];

export const LOOSE_PAGES: ILoosePage[] = [
  {
    path: '/deployments/builds/:buildId',
    render: (p) => <PageBuildDetails buildId={p.match.params.buildId} />
  } as ILoosePage<{ buildId: string }>,
  {
    path: '/deployments/services/:env/:name',
    render: (p) => (
      <PageServiceDetails env={p.match.params.env} name={p.match.params.name} />
    )
  } as ILoosePage<{ env: string; name: string }>,

  {
    path: '/orgs/:orgId',
    render: (p) => <PageOrgDetails orgId={p.match.params.orgId} />
  } as ILoosePage<{ orgId: string }>,

  {
    path: '/orgs/:orgId/experience-templates/:tplId',
    render: (p) => (
      <PageOrgExperienceTemplateDetails
        orgId={p.match.params.orgId}
        tplId={p.match.params.tplId}
      />
    )
  } as ILoosePage<{ orgId: string; tplId: string }>,

  {
    path: '/orgs/:orgId/experience-templates/:tplId/audit-log',
    render: (p) => (
      <PageOrgExperienceTemplateDetailsAuditLog
        orgId={p.match.params.orgId}
        tplId={p.match.params.tplId}
      />
    )
  } as ILoosePage<{ orgId: string; tplId: string }>,

  {
    path: '/orgs/:orgId/schedules-hanging',
    render: (p) => <PageOrgSchedulesHanging orgId={p.match.params.orgId} />
  } as ILoosePage<{ orgId: string; tplId: string }>,

  {
    path: '/ai-usage/:orgId',
    render: (p) => <PageAiUsageOrgDetails orgId={p.match.params.orgId} />
  } as ILoosePage<{ orgId: string }>
];

export interface ICategory {
  id: string;
  route: string;
  icon: JSX.Element;
  disabled?: boolean;
}

export interface ICategoryGroup {
  id: string;
  children: ICategory[];
}

const iconProps = {
  size: 20,
  strokeWidth: 1.5,
  color: '#fff'
};

export const NAV: ICategoryGroup[] = [
  {
    id: 'Data',
    children: [
      { id: 'Orgs', icon: <IconOrgs {...iconProps} />, route: '/orgs' },

      {
        id: 'Experiences',
        icon: <IconExperiences {...iconProps} />,
        route: '/experiences'
      },
      {
        id: 'Moments',
        icon: <IconMoments {...iconProps} />,
        route: '/moments'
      },
      {
        id: 'Schedules',
        icon: <IconSchedules {...iconProps} />,
        route: '/schedules'
      },

      {
        id: 'Late reminders',
        icon: <IconSchedules {...iconProps} />,
        route: '/late-reminders'
      },
      {
        id: 'Admin Users',
        icon: <IconAdminUsers {...iconProps} />,
        route: '/admin-users'
      },
      { id: 'Ops', icon: <IconOps {...iconProps} />, route: '/ops' }
    ]
  },
  {
    id: 'Admin',
    children: [
      {
        id: 'Feature Flags',
        icon: <IconFlag {...iconProps} />,
        route: '/admin'
      },
      { id: 'Config', icon: <IconSettings {...iconProps} />, route: '/config' }
    ]
  },
  {
    id: 'Deployments',
    children: [
      {
        id: 'Services',
        icon: <IconServices {...iconProps} />,
        route: '/deployments/services'
      },
      {
        id: 'Builds',
        icon: <IconBuilds {...iconProps} />,
        route: '/deployments/builds'
      }
    ]
  },
  {
    id: 'Business',
    children: [
      {
        id: 'Pricing Calculator',
        icon: <IconPricing {...iconProps} />,
        route: '/pricing'
      },
      {
        id: 'AI Usage',
        icon: <IconAiUsage {...iconProps} />,
        route: '/ai-usage'
      }
    ]
  }
];
